
import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { GrowthChart } from "./GrowthChart";
import { createChartConfig } from "./chart/ChartConfig";
import { Toast } from "@/components/ui/toast";
import { averageMilkIntakeByAge } from "./chart/data/MilkIntakeData";
import { Milk } from "lucide-react";

// Reference data for average milk intake by age in months (ml)
const getAverageMilkIntakeForAge = (ageMonths: number): number => {
  return averageMilkIntakeByAge(ageMonths);
};

interface MilkChartProps {
  milkData: any[];
  useMetricUnit: boolean; // Always true for milk (ml/oz)
  handleUnitToggle: () => void;
  babyAgeInMonths: number;
}

export function MilkChart({
  milkData,
  useMetricUnit,
  handleUnitToggle,
  babyAgeInMonths
}: MilkChartProps) {
  const { language } = useLanguage();
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Set up unit-based variables
  const unitLabel = useMetricUnit ? "ml" : "oz";
  const valueKey = "displayMilkIntake";
  
  // For chart configuration
  const chartConfig = createChartConfig(
    "milk",
    t('statistics.milkIntake', language),
    {
      light: "#06b6d4", // Cyan color for milk
      dark: "#22d3ee"
    }
  );
  
  // Handle transition effects when props change
  useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => setIsTransitioning(false), 300);
    return () => clearTimeout(timer);
  }, [useMetricUnit, milkData.length]);

  return (
    <GrowthChart
      title={t('statistics.milkIntakeChartTitle', language)}
      description={t('statistics.milkIntakeProgress', language)}
      chartData={milkData}
      displayOriginalValue={null} // No birth milk intake value
      useMetricUnit={useMetricUnit}
      handleUnitToggle={handleUnitToggle}
      unitLabel={unitLabel}
      averageData={[]} // We'll use getAverageValueForAge function instead
      getAverageValueForAge={getAverageMilkIntakeForAge}
      valueKey={valueKey}
      chartConfig={chartConfig}
      babyAgeInMonths={babyAgeInMonths}
      icon={<div className="p-2 bg-cyan-100 rounded-full inline-block"><Milk className="h-5 w-5 text-cyan-500" /></div>}
    />
  );
}
